/**
 * Query-param builders for the operations dashboard's two alarm grids. Each
 * toolbar chip (time window, plant, alarm tree, structure type, status) and
 * the page search box become one key here; `toJsonApiParams` then sends each
 * one as `filter[<key>]` — except `text_search`, which it renames to the
 * reserved `filter[search]`. A key the odl-app viewset does not read is a chip
 * that highlights while the row set stays put, so the names below are the ones
 * `OperationsAlarmsViewSet` / `OperationsResolvedViewSet` parse.
 *
 * The resolved grid takes no `active_only`: Status=Resolved swaps to the
 * repair-log resource instead of narrowing the active one.
 *
 * Contains:
 * - buildOperationsAlarmsParams — active-alarms grid params
 * - buildOperationsResolvedParams — resolved repair-log grid params
 *
 * Returns plain JS — no JSX.
 */

/**
 * @param {object} state
 * @param {number|string} state.customerId - required; the endpoints 400 without it.
 * @param {number} state.hours - lookback window; omitted when not positive.
 * @param {(number|string)[]} state.siteGroupIds - selected plants.
 * @param {string[]} state.selectedAlarms - selected Alarm.key values (the Alarms tree filter;
 *   card clicks populate this directly with the alarms the click's rule matches).
 * @param {string} state.search - debounced free-text term.
 * @param {?string} state.structureFilter - 'digital' | 'static' | null.
 * @returns {object} Params shared by both grids, keyed as the viewsets parse them.
 */
function buildSharedParams({ customerId, hours, siteGroupIds, selectedAlarms, search, structureFilter }) {
  const p = { customer_id: customerId };
  if (hours > 0) p.hours = hours;
  if (siteGroupIds.length > 0) p.site_group_id = siteGroupIds.join(',');
  if (selectedAlarms.length > 0) p.alarm = selectedAlarms.join(',');
  if (search) p.text_search = search;
  if (structureFilter) p.structure_type = structureFilter;
  return p;
}

/**
 * @param {object} state - the shared filter state, plus:
 * @param {string} state.alarmStatus - 'all' | 'active' | 'resolved'.
 * @returns {object} Params for the active-alarms grid.
 */
export function buildOperationsAlarmsParams(state) {
  const p = buildSharedParams(state);
  if (state.alarmStatus === 'active') p.active_only = true;
  return p;
}

/**
 * @param {object} state - the shared filter state.
 * @returns {object} Params for the resolved repair-log grid.
 */
export function buildOperationsResolvedParams(state) {
  return buildSharedParams(state);
}
